import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

const BusinessProfileEditable = () => {
  const navigate = useNavigate();
  const [leftSection, setLeftSection] = useState('business');
  const [bizForm, setBizForm] = useState({
    name: '',
    description: '',
    website: '',
    google_profile_link: '',
    location: ''
  });
  const [logo, setLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [moverData, setMoverData] = useState(null);
  const [showCompanyProfile, setShowCompanyProfile] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showDeleteInsuranceModal, setShowDeleteInsuranceModal] = useState(false);
  const [showDeleteLicenseModal, setShowDeleteLicenseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteInsuranceId, setDeleteInsuranceId] = useState(null);
  const [deleteLicenseId, setDeleteLicenseId] = useState(null);
  const [insurances, setInsurances] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [insuranceForm, setInsuranceForm] = useState({
    insurance_type: '',
    insurance_provider: '',
    policy_number: '',
    coverage_details: '',
    valid_from: '',
    valid_to: ''
  });
  const [licenseForm, setLicenseForm] = useState({
    license_type: '',
    license_number: '',
    issued_by: '',
    valid_from: '',
    valid_to: ''
  });
  const logoInputRef = useRef(null);

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    if (!moverData) return 0;
    
    let completed = 0;
    let total = 11;
    
    // Basic info (5 fields)
    if (moverData.company_name) completed++;
    if (moverData.company_description) completed++;
    if (moverData.website_link) completed++;
    if (moverData.company_logo) completed++;
    if (moverData.google_profile_link) completed++;
    
    // Contact info (3 fields)
    if (moverData.phone) completed++;
    if (moverData.email) completed++;
    if (moverData.business_address) completed++;
    
    // Insurance (1 point) - only count approved insurance
    const approvedInsurances = insurances.filter(ins => ins.status === 'approved');
    if (approvedInsurances.length > 0) completed++;
    
    // Licenses (1 point) - only count approved licenses
    const approvedLicenses = licenses.filter(lic => lic.status === 'approved');
    if (approvedLicenses.length > 0) completed++;
    
    // Location (1 point)
    if (moverData.city && moverData.state) completed++;
    
    return Math.round((completed / total) * 100);
  };

  // Load mover data from Supabase
  useEffect(() => {
    loadMoverData();
  }, []);

  const loadMoverData = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        navigate('/signin-mover');
        return;
      }

      const { data: mover, error: moverError } = await supabase
        .from('movers_data')
        .select('*')
        .eq('email', user.email)
        .single();

      if (moverError) {
        return;
      }

      if (mover) {
        setMoverData(mover);
        setBizForm({
          name: mover.company_name || '',
          description: mover.company_description || '',
          website: mover.website_link || '',
          google_profile_link: mover.google_profile_link || '',
          location: `${mover.city || ''}, ${mover.state || ''}`.trim().replace(/^,|,$/g, '') || ''
        });
        
        // Load logo if exists
        if (mover.company_logo) {
          setLogo(mover.company_logo);
        }

        // Load insurance records
        const { data: insuranceData, error: insuranceError } = await supabase
          .from('mover_insurance')
          .select('*')
          .eq('mover_id', mover.id)
          .order('created_at', { ascending: false });

        if (!insuranceError && insuranceData) {
          setInsurances(insuranceData);
        }

        // Load license records
        const { data: licenseData, error: licenseError } = await supabase
          .from('mover_licenses')
          .select('*')
          .eq('mover_id', mover.id)
          .order('created_at', { ascending: false });

        if (!licenseError && licenseData) {
          setLicenses(licenseData);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const triggerLogoUpload = () => logoInputRef.current?.click();
  
  const onLogoSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|gif)/i.test(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, or GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const url = URL.createObjectURL(file);
    setLogo(url);
    setLogoFile(file);
  };

  const uploadLogo = async () => {
    try {
      setUploading(true);
      
      if (!logoFile || !moverData) return null;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to upload files');
        return null;
      }

      // Create unique filename
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${user.id}_${moverData.id}_${Date.now()}.${fileExt}`;
      const filePath = `mover-logos/${fileName}`;

      // Delete old logo if exists
      if (moverData.company_logo) {
        try {
          const oldPath = moverData.company_logo.split('/storage/v1/object/public/storage/').pop();
          if (oldPath) {
            await supabase.storage.from('storage').remove([oldPath]);
          }
        } catch (error) {
        }
      }

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('storage')
        .upload(filePath, logoFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        
        // If RLS error, provide helpful message
        if (uploadError.message?.includes('row-level security')) {
          toast.error('Storage access denied. Please contact administrator to configure storage policies. You can still save other details without uploading a logo.');
          return null;
        }
        
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('storage')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      toast.error(`Upload failed: ${error.message || 'Unknown error'}. You can still save other details.`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBusinessDetails = async () => {
    try {
      setSaving(true);
      
      if (!moverData) {
        toast.error('Mover data not loaded');
        return;
      }

      // Upload logo if a new file was selected
      let logoUrl = moverData.company_logo;
      if (logoFile) {
        const uploadedUrl = await uploadLogo();
        if (!uploadedUrl) {
          setSaving(false);
          return;
        }
        logoUrl = uploadedUrl;
      }

      // Update mover data in Supabase
      const { error } = await supabase
        .from('movers_data')
        .update({
          company_description: bizForm.description,
          website_link: bizForm.website,
          google_profile_link: bizForm.google_profile_link || null,
          company_logo: logoUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', moverData.id);

      if (error) {
        throw error;
      }

      setLogoFile(null);
      await loadMoverData(); // Reload data
      setSuccessMessage('Business details saved successfully!');
      setShowSuccessModal(true);
      
      // Reload page to update profile completion
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInsurance = async () => {
    try {
      if (!moverData) {
        toast.error('Mover data not loaded');
        return;
      }

      if (!insuranceForm.insurance_provider || !insuranceForm.policy_number) {
        toast.error('Please fill in required fields: Insurance Provider and Policy Number');
        return;
      }

      const { error } = await supabase
        .from('mover_insurance')
        .insert({
          mover_id: moverData.id,
          insurance_type: insuranceForm.insurance_type || null,
          insurance_provider: insuranceForm.insurance_provider,
          policy_number: insuranceForm.policy_number,
          coverage_details: insuranceForm.coverage_details,
          valid_from: insuranceForm.valid_from || null,
          valid_to: insuranceForm.valid_to || null,
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (error) {
        throw error;
      }

      setShowInsuranceModal(false);
      setInsuranceForm({
        insurance_type: '',
        insurance_provider: '',
        policy_number: '',
        coverage_details: '',
        valid_from: '',
        valid_to: ''
      });
      
      // Reload insurance data
      const { data: insuranceData } = await supabase
        .from('mover_insurance')
        .select('*')
        .eq('mover_id', moverData.id)
        .order('created_at', { ascending: false });
      
      if (insuranceData) {
        setInsurances(insuranceData);
      }

      setSuccessMessage('Insurance added successfully!');
      setShowSuccessModal(true);
      
      // Reload page to update profile completion
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error(`Failed to save insurance: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSaveLicense = async () => {
    try {
      if (!moverData) {
        toast.error('Mover data not loaded');
        return;
      }

      if (!licenseForm.license_type || !licenseForm.license_number) {
        toast.error('Please fill in required fields: License Type and License Number');
        return;
      }

      const { error } = await supabase
        .from('mover_licenses')
        .insert({
          mover_id: moverData.id,
          license_type: licenseForm.license_type,
          license_number: licenseForm.license_number,
          issued_by: licenseForm.issued_by,
          valid_from: licenseForm.valid_from || null,
          valid_to: licenseForm.valid_to || null,
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (error) {
        throw error;
      }

      setShowLicenseModal(false);
      setLicenseForm({
        license_type: '',
        license_number: '',
        issued_by: '',
        valid_from: '',
        valid_to: ''
      });
      
      // Reload license data
      const { data: licenseData } = await supabase
        .from('mover_licenses')
        .select('*')
        .eq('mover_id', moverData.id)
        .order('created_at', { ascending: false });
      
      if (licenseData) {
        setLicenses(licenseData);
      }

      setSuccessMessage('License added successfully!');
      setShowSuccessModal(true);
      
      // Reload page to update profile completion
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error(`Failed to save license: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteInsurance = async () => {
    try {
      const { error } = await supabase
        .from('mover_insurance')
        .delete()
        .eq('id', deleteInsuranceId);

      if (error) throw error;

      setInsurances(insurances.filter(ins => ins.id !== deleteInsuranceId));
      setShowDeleteInsuranceModal(false);
      setDeleteInsuranceId(null);
      setSuccessMessage('Insurance deleted successfully!');
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(`Failed to delete: ${error.message}`);
      setShowDeleteInsuranceModal(false);
    }
  };

  const handleDeleteLicense = async () => {
    try {
      const { error } = await supabase
        .from('mover_licenses')
        .delete()
        .eq('id', deleteLicenseId);

      if (error) throw error;

      setLicenses(licenses.filter(lic => lic.id !== deleteLicenseId));
      setShowDeleteLicenseModal(false);
      setDeleteLicenseId(null);
      setSuccessMessage('License deleted successfully!');
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(`Failed to delete: ${error.message}`);
      setShowDeleteLicenseModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading business profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {/* Left sidebar menu */}
      <div className="md:col-span-3 space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
              {logo ? (
                <img src={logo} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm text-gray-500">LOGO</span>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-800 truncate">{bizForm.name}</div>
              <button onClick={() => setShowCompanyProfile(true)} className="text-blue-600 text-sm hover:underline">
                View my profile
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-800 mb-2">Profile completion</div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-orange-500 transition-all duration-300" 
              style={{width: `${calculateProfileCompletion()}%`}} 
            />
          </div>
          <div className="mt-2 text-xs text-gray-600">
            {calculateProfileCompletion()}% complete
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <div className="text-sm font-semibold text-gray-800 px-2 py-2">Business details</div>
          <div className="flex flex-col text-sm">
            <button 
              onClick={() => setLeftSection('business')} 
              className={`text-left px-2 py-2 rounded ${leftSection === 'business' ? 'text-orange-600 font-semibold' : 'text-blue-700 hover:underline'}`}
            >
              Business details
            </button>
            <button 
              onClick={() => setLeftSection('insurance')} 
              className={`text-left px-2 py-2 rounded ${leftSection === 'insurance' ? 'text-orange-600 font-semibold' : 'text-blue-700 hover:underline'}`}
            >
              Insurance and licensing
            </button>
            <button 
              onClick={() => setLeftSection('account')} 
              className={`text-left px-2 py-2 rounded ${leftSection === 'account' ? 'text-orange-600 font-semibold' : 'text-blue-700 hover:underline'}`}
            >
              Account details
            </button>
          </div>
        </div>
      </div>

      {/* Right content */}
      <div className="md:col-span-9 space-y-6">
        {leftSection === 'business' && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 md:p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-800">Business details</h2>
            </div>
            <div className="p-4 md:p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Business name
                </label>
                <input 
                  value={bizForm.name} 
                  readOnly
                  className="w-full border rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed" 
                />
                <p className="text-xs text-gray-500 mt-1">Contact admin to change business name</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Business description
                </label>
                <textarea 
                  rows={5} 
                  value={bizForm.description} 
                  onChange={(e) => setBizForm({...bizForm, description: e.target.value})} 
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="Tell customers about your business..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Website
                </label>
                <input 
                  value={bizForm.website} 
                  onChange={(e) => setBizForm({...bizForm, website: e.target.value})} 
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="https://yourcompany.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">
                  Google Maps Profile Link
                </label>
                <input 
                  type="url"
                  value={bizForm.google_profile_link} 
                  onChange={(e) => setBizForm({...bizForm, google_profile_link: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="https://maps.google.com/?cid=..."
                />
                <p className="text-xs text-gray-500 mt-1">Add your Google Maps business profile link to help complete your profile. You will receive a verified badge after admin review.</p>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-800 mb-2">Upload company logo</div>
                <button 
                  type="button" 
                  onClick={triggerLogoUpload} 
                  className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-gray-800 hover:bg-gray-50"
                  disabled={uploading}
                >
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M3 16a2 2 0 002 2h10a2 2 0 002-2V8a2 2 0 00-2-2h-3l-2-2H5a2 2 0 00-2 2v10z"/>
                  </svg>
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <div className="mt-2 text-xs text-gray-500">Minimum size: 300 x 300 pixels</div>
                <div className="text-xs text-gray-500">Accepted file types: JPG/JPEG, PNG, GIF</div>
                <input 
                  ref={logoInputRef} 
                  onChange={onLogoSelected} 
                  type="file" 
                  accept="image/png,image/jpeg,image/jpg,image/gif" 
                  className="hidden" 
                />
                {logo && (
                  <div className="mt-3">
                    <img src={logo} alt="Logo preview" className="w-32 h-32 object-contain rounded border" />
                  </div>
                )}
              </div>

              <div className="pt-2 flex justify-end">
                <button 
                  onClick={handleSaveBusinessDetails}
                  disabled={saving || uploading}
                  className="px-5 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2"
                >
                  {(saving || uploading) && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {leftSection === 'insurance' && (
          <div className="space-y-6">
            {/* Insurance Section */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 md:p-6 border-b flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Insurance</h2>
                <button 
                  onClick={() => setShowInsuranceModal(true)}
                  className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm"
                >
                  Add new insurance
                </button>
              </div>
              <div className="p-4 md:p-6">
                {insurances.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No insurance records yet</p>
                    <p className="text-sm mt-1">Add your insurance information to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {insurances.map((insurance) => (
                      <div key={insurance.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="font-semibold text-gray-800">
                                {insurance.insurance_type || 'Insurance'} - {insurance.insurance_provider}
                              </div>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                insurance.status === 'approved' ? 'bg-green-100 text-green-700' : 
                                insurance.status === 'rejected' ? 'bg-red-100 text-red-700' : 
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {insurance.status === 'approved' ? '✓ Approved' : 
                                 insurance.status === 'rejected' ? '✗ Rejected' : 
                                 '⏳ Pending Review'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-500">Policy Number:</span>
                                <div className="font-medium">{insurance.policy_number}</div>
                              </div>
                              {insurance.coverage_details && (
                                <div>
                                  <span className="text-gray-500">Coverage:</span>
                                  <div className="font-medium">{insurance.coverage_details}</div>
                                </div>
                              )}
                              {insurance.valid_from && (
                                <div>
                                  <span className="text-gray-500">Valid From:</span>
                                  <div className="font-medium">{new Date(insurance.valid_from).toLocaleDateString()}</div>
                                </div>
                              )}
                              {insurance.valid_to && (
                                <div>
                                  <span className="text-gray-500">Valid To:</span>
                                  <div className="font-medium">{new Date(insurance.valid_to).toLocaleDateString()}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setDeleteInsuranceId(insurance.id);
                              setShowDeleteInsuranceModal(true);
                            }}
                            className="ml-4 text-red-600 hover:text-red-700 p-2"
                            title="Delete insurance"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Licensing Section */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 md:p-6 border-b flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Licensing</h2>
                <button 
                  onClick={() => setShowLicenseModal(true)}
                  className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm"
                >
                  Add new license
                </button>
              </div>
              <div className="p-4 md:p-6">
                {licenses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No license records yet</p>
                    <p className="text-sm mt-1">Add your licensing information to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {licenses.map((license) => (
                      <div key={license.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="font-semibold text-gray-800">{license.license_type}</div>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                license.status === 'approved' ? 'bg-green-100 text-green-700' : 
                                license.status === 'rejected' ? 'bg-red-100 text-red-700' : 
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {license.status === 'approved' ? '✓ Approved' : 
                                 license.status === 'rejected' ? '✗ Rejected' : 
                                 '⏳ Pending Review'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-500">License Number:</span>
                                <div className="font-medium">{license.license_number}</div>
                              </div>
                              {license.issued_by && (
                                <div>
                                  <span className="text-gray-500">Issued By:</span>
                                  <div className="font-medium">{license.issued_by}</div>
                                </div>
                              )}
                              {license.valid_from && (
                                <div>
                                  <span className="text-gray-500">Valid From:</span>
                                  <div className="font-medium">{new Date(license.valid_from).toLocaleDateString()}</div>
                                </div>
                              )}
                              {license.valid_to && (
                                <div>
                                  <span className="text-gray-500">Valid To:</span>
                                  <div className="font-medium">{new Date(license.valid_to).toLocaleDateString()}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setDeleteLicenseId(license.id);
                              setShowDeleteLicenseModal(true);
                            }}
                            className="ml-4 text-red-600 hover:text-red-700 p-2"
                            title="Delete license"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {leftSection === 'account' && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 md:p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-800">Account details</h2>
            </div>
            <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">Company name</div>
                  <div className="text-sm font-medium text-gray-800">{bizForm.name}</div>
                </div>
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">Account email</div>
                  <div className="text-sm font-medium text-gray-800">{moverData?.email || '—'}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">Phone</label>
                <input 
                  value={moverData?.phone || ''}
                  readOnly
                  className="w-full border rounded-md px-3 py-2 bg-gray-100" 
                  placeholder="(201) 555-0123" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">City</label>
                <input 
                  value={moverData?.city || ''}
                  readOnly
                  className="w-full border rounded-md px-3 py-2 bg-gray-100" 
                  placeholder="City" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">State</label>
                <input 
                  value={moverData?.state || ''}
                  readOnly
                  className="w-full border rounded-md px-3 py-2 bg-gray-100" 
                  placeholder="State" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">Zip Code</label>
                <input 
                  value={moverData?.zip_code || ''}
                  readOnly
                  className="w-full border rounded-md px-3 py-2 bg-gray-100" 
                  placeholder="Zip Code" 
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-800 mb-1">Address</label>
                <input 
                  value={moverData?.business_address || ''}
                  readOnly
                  className="w-full border rounded-md px-3 py-2 bg-gray-100" 
                  placeholder="Street, City, State, ZIP" 
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Company Profile Modal */}
      {showCompanyProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">Company profile</h3>
                <button
                  onClick={() => setShowCompanyProfile(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                  {logo ? (
                    <img src={logo} alt="Logo" className="w-full h-full object-cover"/>
                  ) : (
                    <span className="text-sm text-gray-500">LOGO</span>
                  )}
                </div>
                <div className="font-semibold text-gray-800">{bizForm.name}</div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1 font-medium">Website</label>
                <a href={bizForm.website} target="_blank" rel="noreferrer" className="text-blue-600 break-all">
                  {bizForm.website}
                </a>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1 font-medium">About</label>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">{bizForm.description}</div>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowCompanyProfile(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insurance Modal */}
      {showInsuranceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">Add Insurance</h3>
              <button onClick={() => setShowInsuranceModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Type</label>
                <select 
                  value={insuranceForm.insurance_type || ''}
                  onChange={(e) => setInsuranceForm({...insuranceForm, insurance_type: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">Select type</option>
                  <option value="General Liability">General Liability</option>
                  <option value="Cargo Insurance">Cargo Insurance</option>
                  <option value="Workers Compensation">Workers Compensation</option>
                  <option value="Commercial Auto">Commercial Auto</option>
                  <option value="Property Insurance">Property Insurance</option>
                  <option value="Professional Liability">Professional Liability</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Provider <span className="text-red-500">*</span></label>
                <input 
                  type="text"
                  value={insuranceForm.insurance_provider}
                  onChange={(e) => setInsuranceForm({...insuranceForm, insurance_provider: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="e.g., State Farm, Allstate"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number <span className="text-red-500">*</span></label>
                <input 
                  type="text"
                  value={insuranceForm.policy_number}
                  onChange={(e) => setInsuranceForm({...insuranceForm, policy_number: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="Enter policy number"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Details</label>
                <textarea 
                  value={insuranceForm.coverage_details}
                  onChange={(e) => setInsuranceForm({...insuranceForm, coverage_details: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="e.g., $1,000,000 coverage amount"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                <input 
                  type="date"
                  value={insuranceForm.valid_from}
                  onChange={(e) => setInsuranceForm({...insuranceForm, valid_from: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
                <input 
                  type="date"
                  value={insuranceForm.valid_to}
                  onChange={(e) => setInsuranceForm({...insuranceForm, valid_to: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="p-6 border-t flex gap-3 justify-end">
              <button 
                onClick={() => setShowInsuranceModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveInsurance}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
              >
                Save Insurance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* License Modal */}
      {showLicenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">Add License</h3>
              <button onClick={() => setShowLicenseModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Type <span className="text-red-500">*</span></label>
                <select 
                  value={licenseForm.license_type}
                  onChange={(e) => setLicenseForm({...licenseForm, license_type: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  required
                >
                  <option value="">Select license type</option>
                  <option value="USDOT Number">USDOT Number</option>
                  <option value="Motor Carrier (MC) Number">Motor Carrier (MC) Number</option>
                  <option value="State Moving License">State Moving License</option>
                  <option value="Business License">Business License</option>
                  <option value="Commercial Driver's License">Commercial Driver's License</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Number <span className="text-red-500">*</span></label>
                <input 
                  type="text"
                  value={licenseForm.license_number}
                  onChange={(e) => setLicenseForm({...licenseForm, license_number: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="Enter license number"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issued By</label>
                <input 
                  type="text"
                  value={licenseForm.issued_by}
                  onChange={(e) => setLicenseForm({...licenseForm, issued_by: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="e.g., California DMV, FMCSA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                <input 
                  type="date"
                  value={licenseForm.valid_from}
                  onChange={(e) => setLicenseForm({...licenseForm, valid_from: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
                <input 
                  type="date"
                  value={licenseForm.valid_to}
                  onChange={(e) => setLicenseForm({...licenseForm, valid_to: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="p-6 border-t flex gap-3 justify-end">
              <button 
                onClick={() => setShowLicenseModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveLicense}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
              >
                Save License
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Insurance Confirmation Modal */}
      {showDeleteInsuranceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Delete Insurance?</h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete this insurance record? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowDeleteInsuranceModal(false);
                    setDeleteInsuranceId(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDeleteInsurance}
                  className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete License Confirmation Modal */}
      {showDeleteLicenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Delete License?</h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete this license record? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowDeleteLicenseModal(false);
                    setDeleteLicenseId(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDeleteLicense}
                  className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Success!</h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                {successMessage}
              </p>
              <button 
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProfileEditable;
